import React, { useState } from 'react';
import { Key, Save, Loader2, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthClient from '../../auth/data/api/AuthClient';
import { useAuthContext } from '../../auth/data/context/AuthContext';
import Alert, { AlertType } from '../../../shared/components/Alert';
import Loader from '../../../shared/components/Loader';
import '../../profile/styles/ProfilePage.css';
import '../../profile/styles/WhiteLabelPage.css';
import '../components/ProfileEditForm.css';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: AlertType;
  }>({
    show: false,
    title: '',
    message: '',
    type: 'success'
  });

  const closeAlert = () => setAlertConfig(prev => ({ ...prev, show: false }));

  const handleSuccessAck = async () => {
    closeAlert();
    await logout();
    navigate('/', { replace: true });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      await AuthClient.changePassword(password);
      setAlertConfig({
        show: true,
        title: 'Success',
        message: 'Your password has been changed successfully. Please log in again with your new password.',
        type: 'success'
      });
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'Failed to change password. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pe-page">
      <Loader isLoading={loading} message="Updating password..." />
      <div className="pe-main">
        <div className="wl-header">
          <div className="wl-header-info">
            <div className="wl-title-row">
              <h1>Change Password</h1>
              <span className="wl-header-desc">Update your account password.</span>
            </div>
          </div>
        </div>

        {errorMsg && <div className="pf-error-message" style={{ marginBottom: '16px' }}>{errorMsg}</div>}

        <div className="pf-info-sections">
          <div className="pf-section-card">
            <div className="pf-section-header">
              <ShieldCheck size={18} />
              <h3>Password Details</h3>
            </div>
            <div className="pf-field-grid">
              <div className="pf-field-item">
                <label className="pf-field-label">
                  <Key size={14} />
                  New Password
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pf-input-box"
                    placeholder="Enter new password"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', padding: 0 }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="pf-field-item">
                <label className="pf-field-label">
                  <Key size={14} />
                  Confirm Password
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pf-input-box"
                    placeholder="Confirm new password"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', padding: 0 }}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pe-footer">
        <button className="wl-btn-primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <Loader2 size={16} className="pf-spin" /> : <Save size={16} />}
          Update Password
        </button>
      </div>

      <Alert
        show={alertConfig.show}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={closeAlert}
        primaryButtonText="Login"
        onPrimaryClick={alertConfig.type === 'success' ? handleSuccessAck : closeAlert}
      />
    </div>
  );
};

export default ChangePasswordPage;
